import type { Currency, PromotionPlanId, PromotionStatus } from '@talkofthetown/shared';
import { config } from '../config';
import { callables } from '../firebase';

/** What Razorpay's checkout script offers (only the part used here). */
interface RazorpayCheckout {
  open(): void;
}
interface RazorpayResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}
declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => RazorpayCheckout;
  }
}

/** The emulators' gateway accepts this signature (functions/src/core/payments.ts). */
const FAKE_SIGNATURE = 'fake-signature';

let script: Promise<void> | null = null;

/** Razorpay's checkout, loaded the first time someone pays. */
function loadCheckout(): Promise<void> {
  script ??= new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      script = null;
      reject(new Error('The payment page could not load. Check your connection and try again.'));
    };
    document.head.append(s);
  });
  return script;
}

/**
 * Waits until no modal dialog is open (one closing animates away first): a modal dialog sits above
 * everything, Razorpay's checkout included, and makes the rest of the page unclickable.
 */
async function noModalOpen(timeoutMs = 3000): Promise<void> {
  const start = Date.now();
  while (document.querySelector('dialog[open]') && Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, 50));
  }
}

/** The reader closed the payment window without paying. */
export class PaymentCancelled extends Error {
  constructor() {
    super('Payment cancelled');
    this.name = 'PaymentCancelled';
  }
}

/**
 * Pays for promoting a post: the server opens an order, the reader pays in Razorpay's checkout
 * (UPI, cards, netbanking, wallets), and the server checks the payment. Resolves with the
 * promotion's status ('review': paid, waiting for an admin). Close any dialog first: the checkout
 * opens once none is open.
 */
export async function payForPromotion(
  postId: string,
  plan: PromotionPlanId,
  currency: Currency,
): Promise<PromotionStatus> {
  const { data: order } = await callables.startPromotion({ postId, plan, currency });
  if (config.payments === 'fake') {
    const res = await callables.confirmPromotion({
      orderId: order.orderId,
      paymentId: `pay_fake${Date.now()}`,
      signature: FAKE_SIGNATURE,
    });
    return res.data.status;
  }
  await Promise.all([loadCheckout(), noModalOpen()]);
  const paid = await new Promise<RazorpayResponse>((resolve, reject) => {
    new window.Razorpay!({
      key: order.keyId,
      order_id: order.orderId,
      amount: order.amount,
      currency: order.currency,
      name: 'Talk of the Town',
      description: `Promote your post: ${order.description}`,
      prefill: { name: order.name ?? undefined, email: order.email ?? undefined },
      theme: { color: '#f46a6a' },
      handler: resolve,
      modal: { ondismiss: () => reject(new PaymentCancelled()) },
    }).open();
  });
  const res = await callables.confirmPromotion({
    orderId: paid.razorpay_order_id,
    paymentId: paid.razorpay_payment_id,
    signature: paid.razorpay_signature,
  });
  return res.data.status;
}
